import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

const ConfirmContext = createContext(null);

export const ConfirmProvider = ({ children }) => {
  const [state, setState] = useState({
    open: false,
    title: 'Êtes-vous sûr ?',
    description: '',
    confirmText: 'Confirmer',
    cancelText: 'Annuler',
    destructive: false,
  });
  const resolver = useRef(null);

  const confirm = useCallback((options) => {
    return new Promise((resolve) => {
      resolver.current = resolve;
      setState({
        open: true,
        title: options.title || 'Êtes-vous sûr ?',
        description: typeof options === 'string' ? options : (options.description || ''),
        confirmText: options.confirmText || 'Confirmer',
        cancelText: options.cancelText || 'Annuler',
        destructive: options.destructive !== false,
      });
    });
  }, []);

  const handle = (answer) => {
    if (resolver.current) {
      resolver.current(answer);
      resolver.current = null;
    }
    setState((s) => ({ ...s, open: false }));
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={state.open} onOpenChange={(o) => !o && handle(false)}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{state.title}</AlertDialogTitle>
            <AlertDialogDescription className="whitespace-pre-line">
              {state.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => handle(false)}
              data-testid="confirm-dialog-cancel"
            >
              {state.cancelText}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handle(true)}
              data-testid="confirm-dialog-confirm"
              className={state.destructive ? 'bg-red-600 hover:bg-red-500 focus:ring-red-500' : 'bg-blue-600 hover:bg-blue-500'}
            >
              {state.confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within ConfirmProvider');
  return ctx;
};
